import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeftRight } from "lucide-react";
import Modal from "@/components/Modal";
import { Btn, Input, Select, Textarea, Field } from "@/components/common";
import RecordPicker from "@/components/RecordPicker";
import { api, apiError } from "@/lib/api";

const REL_TYPES = ["knows", "spouse", "partner", "family", "friend", "colleague", "introduced_by", "works_for", "owns", "member_of", "neighbor_of", "supplies", "collaborates_with", "participated_same_event", "located_beside", "participates_in", "links_to", "other"];
const REL_STATUS = ["confirmed", "reported", "inferred", "disputed", "ended"];
const MUTUAL_TYPES = new Set(["spouse", "partner", "friend", "colleague", "family", "knows", "neighbor_of", "collaborates_with", "participated_same_event"]);
// Contextual "link to whom?" labels keyed by relationship type.
const LABELS = {
  spouse: "Spouse of whom?", partner: "Partner of whom?", family: "Family member — who?",
  friend: "Friend — who?", colleague: "Colleague — who?", works_for: "Works for which organization?",
  owns: "Owns which organization/business?", member_of: "Member of which group/organization?",
  introduced_by: "Introduced by whom?", neighbor_of: "Neighbor of whom?", supplies: "Supplies to whom?",
  collaborates_with: "Collaborates with whom?", located_beside: "Located beside what?",
  participates_in: "Participates in what?", links_to: "Links to which record?", knows: "Knows whom?",
};
const ORG_TYPES = new Set(["works_for", "owns", "member_of"]);

export default function ConnectionModal({ workspaceId, source, editRel, onClose, onSaved }) {
  const isEdit = !!editRel;
  const [target, setTarget] = useState(isEdit ? { id: editRel.target_id, name: editRel.target_name } : null);
  const [targetError, setTargetError] = useState("");
  const [rel, setRel] = useState(isEdit
    ? { type: editRel.type, custom_label: editRel.custom_label || "", direction: editRel.direction || "directed", status: editRel.status || "reported", source: editRel.source || "", valid_from: editRel.valid_from ? editRel.valid_from.slice(0, 10) : "", notes: editRel.notes || "" }
    : { type: "knows", custom_label: "", direction: "mutual", status: "reported", source: "", valid_from: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const setR = (k, v) => setRel((p) => ({ ...p, [k]: v }));

  const onTypeChange = (t) => {
    // Role-specific types keep their direction; symmetric ones default to mutual.
    setRel((p) => ({ ...p, type: t, direction: MUTUAL_TYPES.has(t) ? "mutual" : (p.direction === "mutual" ? "directed" : p.direction) }));
  };

  const contextLabel = LABELS[rel.type] || "Connected to which contact?";
  const targetName = isEdit ? (editRel.target_name || "…") : (target?.name || "…");

  const save = async () => {
    if (rel.type === "other" && !rel.custom_label.trim()) return toast.error("Describe the 'other' relationship");
    if (!isEdit && !target?.id) { setTargetError(contextLabel + " Select, create, or choose Me."); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/relationships/${editRel.id}?workspace_id=${workspaceId}`, {
          source_id: editRel.source_id, target_id: editRel.target_id, type: rel.type, custom_label: rel.custom_label,
          direction: rel.direction, status: rel.status, source: rel.source,
          valid_from: rel.valid_from ? new Date(rel.valid_from).toISOString() : null, notes: rel.notes,
        });
        toast.success("Connection updated"); onSaved(); return;
      }
      await api.post(`/relationships?workspace_id=${workspaceId}`, {
        source_id: source.id, target_id: target.id, type: rel.type, custom_label: rel.custom_label,
        direction: rel.direction, status: rel.status, source: rel.source,
        valid_from: rel.valid_from ? new Date(rel.valid_from).toISOString() : null, notes: rel.notes,
      });
      toast.success("Connection saved"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit connection" : "Add connection"} width={600} testid="connection-modal">
      <div className="text-sm mb-4 flex items-center gap-2 flex-wrap p-3" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid="conn-preview">
        <span style={{ color: "var(--si-text)" }}>{isEdit ? editRel.source_name : source.name}</span>
        {rel.direction === "mutual" ? <ArrowLeftRight size={14} style={{ color: "var(--si-silver)" }} /> : <ArrowRight size={14} style={{ color: "var(--si-silver)" }} />}
        <span className="font-mono text-xs" style={{ color: "var(--si-teal-light)" }}>{rel.type === "other" ? (rel.custom_label || "other") : rel.type.replace(/_/g, " ")}</span>
        {rel.direction === "mutual" ? <ArrowLeftRight size={14} style={{ color: "var(--si-silver)" }} /> : <ArrowRight size={14} style={{ color: "var(--si-silver)" }} />}
        <span style={{ color: "var(--si-text)" }}>{targetName}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <Field label="Relationship"><Select data-testid="conn-type" value={rel.type} onChange={(e) => onTypeChange(e.target.value)}>{REL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select></Field>
        <Field label="Direction" hint={MUTUAL_TYPES.has(rel.type) ? "Symmetric — shown both ways." : "Role-specific — keeps its direction."}><Select value={rel.direction} onChange={(e) => setR("direction", e.target.value)}><option value="directed">directed (→)</option><option value="mutual">mutual (↔)</option></Select></Field>
      </div>
      {rel.type === "other" && <Field label="Describe relationship"><Input data-testid="conn-custom" value={rel.custom_label} onChange={(e) => setR("custom_label", e.target.value)} placeholder="user-defined description" /></Field>}

      {!isEdit && (
        <div className="mb-3">
          <RecordPicker workspaceId={workspaceId} label={contextLabel} value={target}
            onChange={(v) => { setTarget(v); setTargetError(""); }} allowMe allowUnset={false}
            typeFilter={ORG_TYPES.has(rel.type) ? ["business", "organization", "group"] : null}
            excludeId={source.id} required error={targetError} testid="conn-target" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Verification" hint="Proximity or shared membership alone is not a personal relationship."><Select value={rel.status} onChange={(e) => setR("status", e.target.value)}>{REL_STATUS.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
        <Field label="Date (optional)"><Input type="date" value={rel.valid_from} onChange={(e) => setR("valid_from", e.target.value)} /></Field>
      </div>
      <Field label="Source"><Input data-testid="conn-source" value={rel.source} onChange={(e) => setR("source", e.target.value)} placeholder="How was this learned? e.g. 'Met at chamber mixer'" /></Field>
      <Field label="Notes"><Textarea value={rel.notes} onChange={(e) => setR("notes", e.target.value)} /></Field>
      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="conn-save">{busy ? "Saving…" : (isEdit ? "Save changes" : "Save connection")}</Btn>
    </Modal>
  );
}
